import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckSquare } from 'lucide-react';
import { api } from './api';
import { useAuth } from './AuthContext';
import { useLang } from './LanguageContext';
import TopNavPanel, { pressedOutside } from './components/TopNavPanel';
import { dueState, formatShortDate } from './kit/time';

const POLL_MS = 60000;
const PAGE = 200; // the server's page cap; open tasks come back oldest-due first
const SHOWN = 8;

// The viewer's local calendar date, YYYY-MM-DD.
const localDay = (d) => [d.getFullYear(), d.getMonth() + 1, d.getDate()]
  .map((n) => String(n).padStart(2, '0')).join('-');

// Ticket 196: the top-bar Tasks icon. The badge counts your open tasks that
// are overdue or due today (the same calendar-day rule the Tasks page uses);
// the dropdown lists them and ends with "View all". Optionally controlled
// (`open` + `onOpenChange`) so the top nav keeps one menu open at a time.
// Ticket 232: the badge polls GET /tasks/due-count (the server applies the
// dueState rule from the viewer's date and offset); the list loads on open.
export default function TasksMenu({ open: openProp, onOpenChange, className = '' } = {}) {
  const { t } = useLang();
  const { user } = useAuth();
  const me = user?.user_id;
  const [openLocal, setOpenLocal] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : openLocal;
  const setOpen = (v) => {
    if (!controlled) setOpenLocal(v);
    onOpenChange?.(v);
  };
  const [due, setDue] = useState(null); // null until the open menu's list arrives
  const [count, setCount] = useState(0);
  const containerRef = useRef(null);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    if (!me) return;
    try {
      const { data } = await api.get('/tasks', {
        params: { completed: false, owner_id: me, sort: 'due', limit: PAGE },
      });
      setDue((data.items || []).filter((t) => dueState(t.due_date)));
    } catch { setDue([]); /* likely 401 during logout */ }
  }, [me]);

  const loadCount = useCallback(async () => {
    if (!me) return;
    try {
      const now = new Date();
      const { data } = await api.get('/tasks/due-count', {
        params: { today: localDay(now), tz_offset: now.getTimezoneOffset() },
      });
      setCount(data.count || 0);
    } catch { /* ignore silently — likely 401 during logout */ }
  }, [me]);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, POLL_MS);
    return () => clearInterval(t);
  }, [loadCount]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => { if (pressedOutside(e, [containerRef, panelRef])) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = () => {
    const nowOpen = !open;
    setOpen(nowOpen);
    if (nowOpen) { load(); loadCount(); }
  };
  const go = (to) => { setOpen(false); navigate(to); };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <button
        type="button"
        onClick={toggle}
        data-testid="topnav-tasks"
        title={t('nav.tasks')}
        aria-label={t('nav.tasks')}
        aria-haspopup="menu"
        aria-expanded={open}
        className="relative w-8 h-8 rounded-md flex items-center justify-center text-white/70 hover:text-white hover:bg-white/5 transition"
      >
        <CheckSquare size={16} />
        {count > 0 && (
          <span
            data-testid="tasks-badge"
            className="absolute top-1 right-1 min-w-[14px] h-[14px] px-1 rounded-full text-[9px] font-bold flex items-center justify-center"
            style={{ background: '#6BA82E', color: '#030524' }}
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <TopNavPanel
          ref={panelRef}
          anchorRef={containerRef}
          onDismiss={() => setOpen(false)}
          stretchOnPhone
          gap={8}
          data-testid="tasks-panel"
          className="sm:w-80 rounded-xl border shadow-2xl"
          style={{ background: '#ffffff', borderColor: 'rgba(7,11,95,0.15)', boxShadow: '0 12px 40px rgba(7,11,95,0.25)' }}
        >
          <div className="h-[3px]" style={{ background: 'linear-gradient(90deg, #6BA82E 0%, #8bc94f 60%, #6BA82E 100%)' }} />
          <div className="px-4 py-3 border-b border-slate-100">
            <div className="font-semibold text-[13px]" style={{ color: '#070B5F' }}>{t('nav.tasks')}</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold">
              {count > 0 ? `${count} due today or overdue` : 'Nothing due today'}
            </div>
          </div>
          <div className="max-h-[360px] overflow-y-auto">
            {due === null && (
              <div className="px-4 py-3 text-[12px] text-slate-400" data-testid="tasks-loading">Loading…</div>
            )}
            {(due || []).slice(0, SHOWN).map((t) => {
              const overdue = dueState(t.due_date) === 'overdue';
              return (
                <button
                  type="button"
                  key={t.task_id}
                  onClick={() => go(`/portal/tasks?q=${encodeURIComponent(t.title)}`)}
                  data-testid={`tasks-item-${t.task_id}`}
                  className="w-full flex items-start justify-between gap-3 px-4 py-2.5 border-b border-slate-100 hover:bg-slate-50 text-left transition"
                >
                  <span className="min-w-0">
                    <span className="block text-[13px] font-semibold leading-tight truncate" style={{ color: '#070B5F' }}>{t.title}</span>
                    {(t.contact_name || t.lead_name) && (
                      <span className="block text-[11px] text-slate-500 truncate">{t.contact_name || t.lead_name}</span>
                    )}
                  </span>
                  <span className={`shrink-0 text-[10px] uppercase tracking-widest font-semibold ${overdue ? 'text-red-600' : 'text-slate-500'}`}>
                    {overdue ? `Overdue · ${formatShortDate(t.due_date)}` : 'Today'}
                  </span>
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => go('/portal/tasks')}
              data-testid="tasks-view-all"
              className="w-full px-4 py-3 text-center text-[12px] font-semibold hover:bg-slate-50 transition"
              style={{ color: '#070B5F' }}
            >
              View all
            </button>
          </div>
        </TopNavPanel>
      )}
    </div>
  );
}
